import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Calendar,
  IndianRupee,
  TrendingUp,
  GraduationCap,
  Clock,
  Home
} from 'lucide-react';

// Import all HRMS modules
import {
  EmployeeList,
  AddEmployee,
  EmployeeProfile,
  EditEmployee,
  EmployeeTransfer,
  BulkUpload,
  DocumentManager,
  TransferRoleChange
} from './employee-database';
import DepartmentRoleMapping from './system/DepartmentRoleMapping';

import {
  RecruitmentJobs,
  CandidatePipeline,
  InterviewScheduling,
  OfferManagement
} from './recruitment';

import {
  AttendanceDashboard,
  AttendanceLogs,
  Timesheet,
  AttendanceRegularization,
  LeaveRequests,
  LeavePolicies,
  HolidayCalendar,
  ShiftRoster
} from './attendance-leaves';

import TrainingModule from './TrainingModule';
import ExitModule from './ExitModule';
import CompliancePolicyModule from './CompliancePolicyModule';

import PayrollDashboard from './payroll/PayrollDashboard';
import SalaryStructures from './payroll/SalaryStructures';
import PayrollRun from './payroll/PayrollRun';
import PayslipManagement from './payroll/PayslipManagement';
import FullFinalSettlement from './payroll/FullFinalSettlement';

import PerformanceCycles from './performance/PerformanceCycles';
import GoalManagement from './performance/GoalManagement';
import ReviewManagement from './performance/ReviewManagement';
import PerformanceOutcomes from './performance/PerformanceOutcomes';

import { PolicyLibrary, Acknowledgments, AuditLogs } from './compliance-policy';

import { ExitRequests, NoDuesClearance, RelievingLetter, ExperienceCertificate } from './exit-management';

import {
  HRAnalyticsDashboard,
  AttritionAnalysis,
  RecruitmentFunnel,
  SalaryTrends,
  ComplianceReminders,
  AuditLogs as AnalyticsAuditLogs
} from './analytics-reporting';

const HRMSContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Update currentView based on URL path or query params
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');

    if (viewParam) {
      // If view is specified in query params, use it
      setCurrentView(viewParam);
    } else if (pathSegments.length > 1) {
      const module = pathSegments[1];
      const subModule = pathSegments[2];
      setCurrentView(subModule || module);
    } else {
      setCurrentView('dashboard');
    }
  }, [location.pathname, location.search]);

  const handleNavigate = (view, data = null) => {
    const resolvedView = view;
    setCurrentView(resolvedView);
    if (data && data.employeeId) {
      setSelectedEmployeeId(data.employeeId);
    }

    // Update breadcrumbs
    const newBreadcrumbs = [
      { label: 'HRMS', path: '/hrms' },
      { label: getModuleLabel(resolvedView), path: `/hrms/${resolvedView}` }
    ];
    setBreadcrumbs(newBreadcrumbs);
  };

  const handleBack = () => {
    if (breadcrumbs.length > 1) {
      const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
      navigate(previousBreadcrumb.path);
    } else {
      navigate('/hrms');
      setCurrentView('dashboard');
    }
    setSelectedEmployeeId(null);
  };

  const getModuleLabel = (view) => {
    const labels = {
      dashboard: 'Dashboard',
      employee_list: 'Employee List',
      add_employee: 'Add Employee',
      employee_profile: 'Profile',
      edit_employee: 'Edit Employee',
      employee_transfer: 'Employee Transfer',
      bulk_upload: 'Bulk Upload',
      document_manager: 'Document Manager',
      department_role_mapping: 'Department & Role Mapping',
      transfer_role_change: 'Transfer & Role Change',
      recruitment: 'Recruitment',
      job_posts: 'Job Posts',
      candidate_pipeline: 'Candidate Pipeline',
      interview_scheduling: 'Interview Scheduling',
      offer_management: 'Offer Management',
      attendance_dashboard: 'My Attendance',
      attendance_logs: 'Attendance Logs',
      timesheet: 'Timesheet',
      attendance_regularization: 'Attendance Regularization',
      leave_requests: 'Leave Requests',
      leave_policies: 'Leave Policies',
      holiday_calendar: 'Holiday Calendar',
      shift_roster: 'Shift Roster',
      payroll_dashboard: 'Payroll Dashboard',
      salary_structures: 'Salary Structures',
      payroll_run: 'Payroll Run',
      payslip_management: 'Payslip Management',
      statutory_compliance: 'Statutory Compliance',
      full_final_settlement: 'Full & Final Settlement',
      performance_cycles: 'Performance Cycles',
      goal_management: 'Goal Management',
      review_management: 'Review Management',
      performance_outcomes: 'Performance Outcomes',
      training_dashboard: 'Training Dashboard',
      training_catalog: 'Training Catalog',
      modules_lessons: 'Modules & Lessons',
      quiz_question_bank: 'Quiz & Question Bank',
      learning_paths: 'Learning Paths',
      assignments_enrollment: 'Assignments & Enrollment',
      sessions_scheduling: 'Sessions & Scheduling',
      attendance_feedback: 'Attendance Management',
      certificates_credits: 'Certificates & Transcripts',
      compliance_renewals: 'Compliance & Expirations',
      reports_analytics: 'Overview & Analytics',
      settings_policies: 'Settings & Policies',
      policy_library: 'Policy Library',
      acknowledgments: 'Acknowledgments',
      exit_requests: 'Exit Requests',
      no_dues_clearance: 'No Dues Clearance',
      relieving_letter: 'Relieving Letter',
      experience_certificate: 'Experience Certificate',
      hr_analytics_dashboard: 'HR Analytics Dashboard',
      attrition_analysis: 'Attrition Analysis',
      recruitment_funnel: 'Recruitment Funnel',
      salary_trends: 'Salary Trends',
      compliance_reminders: 'Compliance Reminders',
      analytics_audit_logs: 'Analytics Audit Logs'
    };
    return labels[view] || view;
  };

  const renderCurrentView = () => {
    switch (currentView) {
      // Employee Database Module
      case 'employee_list':
        return (
          <EmployeeList
            onNavigate={handleNavigate}
          />
        );

      case 'add_employee':
        return (
          <AddEmployee
            onBack={handleBack}
            onSave={(data) => {
              handleBack();
            }}
          />
        );

      case 'employee_profile':
        return (
          <EmployeeProfile
            employeeId={selectedEmployeeId}
            onBack={handleBack}
            onEdit={(id) => handleNavigate('edit_employee', { employeeId: id })}
          />
        );

      case 'edit_employee':
        return (
          <EditEmployee
            employeeId={selectedEmployeeId}
            onBack={handleBack}
            onSave={(data) => {
              handleBack();
            }}
          />
        );

      case 'employee_transfer':
        return (
          <EmployeeTransfer
            onBack={handleBack}
          />
        );

      case 'bulk_upload':
        return (
          <BulkUpload
            onBack={handleBack}
          />
        );

      case 'document_manager':
        return (
          <DocumentManager
            onBack={handleBack}
          />
        );

      case 'department_role_mapping':
        return (
          <DepartmentRoleMapping
            onBack={handleBack}
          />
        );

      case 'transfer_role_change':
        return (
          <TransferRoleChange
            onBack={handleBack}
          />
        );

      // Recruitment Module
      case 'recruitment':
      case 'job_posts':
        return (
          <RecruitmentJobs
            onBack={handleBack}
          />
        );

      case 'candidate_pipeline':
        return (
          <CandidatePipeline
            onBack={handleBack}
          />
        );

      case 'interview_scheduling':
        return (
          <InterviewScheduling
            onBack={handleBack}
          />
        );

      case 'offer_management':
        return (
          <OfferManagement
            onBack={handleBack}
          />
        );

      // Attendance & Leave Module
      case 'attendance_dashboard':
        return (
          <AttendanceDashboard
            onBack={handleBack}
          />
        );

      case 'attendance_logs':
        return (
          <AttendanceLogs
            onBack={handleBack}
          />
        );

      case 'timesheet':
        return (
          <Timesheet
            onBack={handleBack}
          />
        );

      case 'attendance_regularization':
        return (
          <AttendanceRegularization
            onBack={handleBack}
          />
        );

      case 'leave_requests':
        return (
          <LeaveRequests
            onBack={handleBack}
          />
        );

      case 'leave_policies':
        return (
          <LeavePolicies
            onBack={handleBack}
          />
        );

      case 'holiday_calendar':
        return (
          <HolidayCalendar
            onBack={handleBack}
          />
        );

      case 'shift_roster':
        return (
          <ShiftRoster
            onBack={handleBack}
          />
        );

      // Payroll Module
      case 'payroll_dashboard':
        return (
          <PayrollDashboard
            onBack={handleBack}
          />
        );

      case 'salary_structures':
        return (
          <SalaryStructures
            onBack={handleBack}
          />
        );

      case 'payroll_run':
        return (
          <PayrollRun
            onBack={handleBack}
          />
        );

      case 'payslip_management':
        return (
          <PayslipManagement
            onBack={handleBack}
          />
        );

      case 'full_final_settlement':
        return (
          <FullFinalSettlement
            onBack={handleBack}
          />
        );

      // Performance Module
      case 'performance_dashboard':
      case 'performance_cycles':
        return (
          <PerformanceCycles
            onBack={handleBack}
          />
        );

      case 'goal_management':
        return (
          <GoalManagement
            onBack={handleBack}
          />
        );

      case 'review_management':
        return (
          <ReviewManagement
            onBack={handleBack}
          />
        );

      case 'performance_outcomes':
        return (
          <PerformanceOutcomes
            onBack={handleBack}
          />
        );

      // Training & Development Module
      case 'training_dashboard':
        return <TrainingModule initialTab="trainingDashboard" />;

      case 'training_catalog':
        return <TrainingModule initialTab="trainingCatalog" />;

      case 'modules_lessons':
        return <TrainingModule initialTab="modulesLessons" />;

      case 'quiz_question_bank':
        return <TrainingModule initialTab="quizQuestionBank" />;

      case 'learning_paths':
        return <TrainingModule initialTab="learningPaths" />;

      case 'assignments_enrollment':
        return <TrainingModule initialTab="assignmentsEnrollment" />;

      case 'sessions_scheduling':
        return <TrainingModule initialTab="sessionsScheduling" />;

      case 'attendance_feedback':
        return <TrainingModule initialTab="attendanceManagement" />;

      case 'certificates_credits':
        return <TrainingModule initialTab="certificatesTranscripts" />;

      case 'compliance_renewals':
        return <TrainingModule initialTab="complianceExpirations" />;

      case 'reports_analytics':
        return <TrainingModule initialTab="overviewAnalytics" />;

      case 'settings_policies':
        return <TrainingModule initialTab="settingsPolicies" />;

      // Compliance & Policy Module
      case 'compliance_policy':
      case 'compliance-policy':
      case 'policy_library':
        return (
          <CompliancePolicyModule initialTab="policyLibrary" />
        );

      case 'acknowledgments':
        return (
          <CompliancePolicyModule initialTab="acknowledgments" />
        );

      case 'statutory_compliance':
        return (
          <CompliancePolicyModule initialTab="statutoryCompliance" />
        );

      // Exit Management Module
      case 'exit_requests':
        return (
          <ExitModule initialTab="exitRequests" />
        );

      case 'no_dues_clearance':
        return (
          <ExitModule initialTab="noDuesClearance" />
        );

      case 'relieving_letter':
        return (
          <ExitModule initialTab="relievingLetter" />
        );

      case 'experience_certificate':
        return (
          <ExitModule initialTab="experienceCertificate" />
        );

      // HR Analytics & Compliance Module
      case 'hr_analytics_dashboard':
        return (
          <HRAnalyticsDashboard
            onBack={handleBack}
          />
        );

      case 'attrition_analysis':
        return (
          <AttritionAnalysis
            onBack={handleBack}
          />
        );

      case 'recruitment_funnel':
        return (
          <RecruitmentFunnel
            onBack={handleBack}
          />
        );

      case 'salary_trends':
        return (
          <SalaryTrends
            onBack={handleBack}
          />
        );

      case 'compliance_reminders':
        return (
          <ComplianceReminders
            onBack={handleBack}
          />
        );

      case 'analytics_audit_logs':
        return (
          <AnalyticsAuditLogs
            onBack={handleBack}
          />
        );

      // Default Dashboard
      case 'dashboard':
      default:
        return (
          <div className="p-6">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
              <p className="text-gray-600">Welcome to the Human Resource Management System</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="h-6 w-6 text-[#0F2942]" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Employees</p>
                    <p className="text-2xl font-bold text-gray-900">1,234</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <UserPlus className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">New Hires</p>
                    <p className="text-2xl font-bold text-gray-900">45</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Leaves</p>
                    <p className="text-2xl font-bold text-gray-900">23</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <GraduationCap className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Trainings</p>
                    <p className="text-2xl font-bold text-gray-900">12</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleNavigate('add_employee')}
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <UserPlus className="h-5 w-5 text-[#0F2942] mr-3" />
                    <span className="text-sm font-medium">Add Employee</span>
                  </button>
                  <button
                    onClick={() => handleNavigate('leave_requests')}
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Calendar className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-sm font-medium">Leave Requests</span>
                  </button>
                  <button
                    onClick={() => handleNavigate('payroll_run')}
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <IndianRupee className="h-5 w-5 text-yellow-600 mr-3" />
                    <span className="text-sm font-medium">Payroll Run</span>
                  </button>
                  <button
                    onClick={() => handleNavigate('performance_cycles')}
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <TrendingUp className="h-5 w-5 text-purple-600 mr-3" />
                    <span className="text-sm font-medium">Performance</span>
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                <div className="space-y-3">
                  <div className="flex items-center text-sm">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="text-gray-600">John Doe joined the company</span>
                    <span className="text-gray-400 ml-auto">2 hours ago</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-gray-600">5 leave requests approved</span>
                    <span className="text-gray-400 ml-auto">4 hours ago</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-gray-600">Monthly payroll processed</span>
                    <span className="text-gray-400 ml-auto">1 day ago</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                    <span className="text-gray-600">Performance review cycle started</span>
                    <span className="text-gray-400 ml-auto">2 days ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      {breadcrumbs.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/hrms')}
              className="flex items-center text-gray-500 hover:text-gray-700"
            >
              <Home className="h-4 w-4 mr-1" />
              HRMS
            </button>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => navigate(breadcrumb.path)}
                  className={`${index === breadcrumbs.length - 1
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {breadcrumb.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1">
        {renderCurrentView()}
      </div>
    </div>
  );
};

export default HRMSContainer;